// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useCurrencyStore } from '@/store/currency.store';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type {
  ICurrencyBody,
  ICurrencyUpdateBody,
} from '@/types/currency.types';

/**
 * Hook pour gérer les mutations liées aux currency.
 * Fournit des fonctions pour créer, mettre à jour et supprimer des currency
 * avec gestion automatique des erreurs globales et du loader.
 */
export const CurrencyMutation = () => {
  const currencyStore = useCurrencyStore();

  // -------------------------------
  // 🔹 Gestion centralisée des erreurs
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0] || 'Une erreur est survenue';

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'un currency
  // -------------------------------
  const createCurrency = useMutation<any, Error, ICurrencyBody>({
    mutationFn: (payload: ICurrencyBody) => {
      return currencyStore.createCurrency(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un curency
  // -------------------------------
  const updateCurrency = useMutation<any, Error, ICurrencyUpdateBody>({
    mutationFn: (payload: ICurrencyUpdateBody) => {
      return currencyStore.updateCurrency(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Suppression d'un taxe
  // -------------------------------
  const deleteCurrency = useMutation<any, Error>({
    mutationFn: () => currencyStore.deleteCurrency(),
    onSuccess: () => {},
    onError: handleError,
  });

  /**
   * Crée une nouvelle currency
   * @param {ICurrencyBody} item - Données du currency
   */
  const handleCreateCurrency = (item: ICurrencyBody) => {
    return createCurrency.mutateAsync(item);
  };
  /**
   * Met à jour un currency existant
   * @param {ICurrencyUpdateBody} item - Données de mise à jour du taxe
   */
  const handleUpdateCurrency = (item: ICurrencyUpdateBody) => {
    return updateCurrency.mutateAsync(item);
  };
  /**
   * Suppression d'un currency existant
   */
  const handleDeleteCurrency = () => {
    return deleteCurrency.mutateAsync();
  };

  return {
    handleCreateCurrency,
    handleUpdateCurrency,
    handleDeleteCurrency,
  };
};
